import * as React from "react";
import { ApiError, setAuthToken, setUnauthorizedHandler } from "@hp/api-client";
import type { User } from "@hp/api-client";
import { getCurrentUser, login as loginRequest } from "../api/auth";

const TOKEN_STORAGE_KEY = "hp_dashboard_auth_token";
/** Set when a sign-in ended on its own (it expired or was revoked), read once by the login page to say so. */
export const SESSION_ENDED_KEY = "hp_dashboard_session_ended";

interface AuthContextValue {
  user: User | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = React.createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = React.useState<User | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    const storedToken = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (!storedToken) {
      setIsLoading(false);
      return;
    }
    setAuthToken(storedToken);
    getCurrentUser(storedToken)
      .then((resolvedUser) => setUser(resolvedUser))
      .catch((error) => {
        localStorage.removeItem(TOKEN_STORAGE_KEY);
        setAuthToken(null);
        if (error instanceof ApiError && error.status === 401) {
          // A tab reopened with an expired sign-in: say so on the login page.
          try {
            sessionStorage.setItem(SESSION_ENDED_KEY, "1");
          } catch {
            // storage refused: the sign-out still happened
          }
        }
      })
      .finally(() => setIsLoading(false));
  }, []);

  const login = React.useCallback(async (email: string, password: string) => {
    const response = await loginRequest(email, password);
    localStorage.setItem(TOKEN_STORAGE_KEY, response.access_token);
    setAuthToken(response.access_token);
    setUser(response.user);
  }, []);

  const logout = React.useCallback(() => {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    setAuthToken(null);
    setUser(null);
  }, []);

  // A request answered 401 means the sign-in is no longer good: sign out and let the login page say why, instead of every form failing alone.
  React.useEffect(() => {
    if (!user) return;
    setUnauthorizedHandler(() => {
      try {
        sessionStorage.setItem(SESSION_ENDED_KEY, "1");
      } catch {
        // a private window may refuse storage: the sign-out still happens
      }
      logout();
    });
    return () => setUnauthorizedHandler(null);
  }, [user, logout]);

  const value = React.useMemo(() => ({ user, isLoading, login, logout }), [user, isLoading, login, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
